import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LogOut, 
  User, 
  Home, 
  Package, 
  FileText, 
  Receipt, 
  Users, 
  BarChart3, 
  Settings,
  Menu,
  X
} from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigationLinks = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/sales/products', icon: Package, label: 'Products' },
    { path: '/sales/history', icon: FileText, label: 'Estimates' },
    { path: '/sales/invoices', icon: Receipt, label: 'Invoices' },
    { path: '/sales/customers', icon: Users, label: 'Customers' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="bg-gradient-to-r from-white via-slate-50 to-white backdrop-blur-sm border-b border-slate-200/60 shadow-lg shadow-slate-200/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-18">
          <div className="flex items-center space-x-10">
            <Link to="/" className="flex-shrink-0 group">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 bg-clip-text text-transparent group-hover:from-indigo-700 group-hover:via-blue-700 group-hover:to-indigo-800 transition-all duration-300">
                IDealtrendz.org
              </h1>
            </Link>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-2">
              {navigationLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`group flex items-center space-x-2 px-4 py-2.5 rounded-2xl text-sm font-semibold transition-all duration-300 ${
                      isActive(link.path)
                        ? 'text-white bg-gradient-to-r from-indigo-600 to-blue-600 shadow-lg shadow-indigo-200/50 scale-105'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/80 hover:shadow-md hover:shadow-slate-200/50 hover:scale-105'
                    }`}
                  >
                    <Icon className={`h-4 w-4 transition-transform duration-300 ${isActive(link.path) ? 'text-white' : 'text-slate-500 group-hover:text-slate-700'} group-hover:scale-110`} />
                    <span className="font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-3 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-sm">
                    <div className="p-1.5 bg-gradient-to-r from-indigo-100 to-blue-100 rounded-xl">
                      <User className="h-4 w-4 text-indigo-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{user.username}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="group flex items-center space-x-2 px-4 py-2.5 text-slate-600 hover:text-red-600 hover:bg-red-50/80 rounded-2xl transition-all duration-300 hover:shadow-md hover:shadow-red-200/50"
                  >
                    <LogOut className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              ) : (
                <Link
                  to="/admin/login"
                  className="group bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white px-6 py-3 rounded-2xl font-semibold transition-all duration-300 shadow-lg shadow-indigo-200/50 hover:shadow-xl hover:shadow-indigo-300/50 hover:scale-105"
                >
                  <span className="group-hover:scale-105 transition-transform duration-300 inline-block">Admin Login</span>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-3 rounded-2xl text-slate-600 hover:text-slate-900 hover:bg-white/80 hover:shadow-md hover:shadow-slate-200/50 transition-all duration-300"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-4 pt-4 pb-6 space-y-3 bg-gradient-to-b from-white/95 to-slate-50/95 backdrop-blur-md border-t border-slate-200/60 shadow-xl shadow-slate-200/50">
              {navigationLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`group flex items-center space-x-4 px-4 py-3 rounded-2xl text-base font-semibold transition-all duration-300 ${
                      isActive(link.path)
                        ? 'text-white bg-gradient-to-r from-indigo-600 to-blue-600 shadow-lg shadow-indigo-200/50'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/80 hover:shadow-md hover:shadow-slate-200/50'
                    }`}
                  >
                    <Icon className={`h-5 w-5 transition-transform duration-300 ${isActive(link.path) ? 'text-white' : 'text-slate-500 group-hover:text-slate-700'} group-hover:scale-110`} />
                    <span className="font-medium">{link.label}</span>
                  </Link>
                );
              })}
              
              {/* Mobile User Menu */}
              <div className="border-t border-slate-200/60 pt-6 mt-6">
                {user ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-4 px-4 py-3 bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-sm">
                      <div className="p-2 bg-gradient-to-r from-indigo-100 to-blue-100 rounded-xl">
                        <User className="h-5 w-5 text-indigo-600" />
                      </div>
                      <span className="text-slate-700 font-semibold">{user.username}</span>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="group flex items-center space-x-4 px-4 py-3 w-full text-left text-slate-600 hover:text-red-600 hover:bg-red-50/80 rounded-2xl transition-all duration-300 hover:shadow-md hover:shadow-red-200/50"
                    >
                      <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/admin/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="group block px-6 py-4 text-center bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white rounded-2xl font-semibold transition-all duration-300 shadow-lg shadow-indigo-200/50 hover:shadow-xl hover:shadow-indigo-300/50"
                  >
                    <span className="group-hover:scale-105 transition-transform duration-300 inline-block">Admin Login</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
